import React, { useState, useEffect, Fragment } from "react";
import moment from "moment";
import { Form, Col, Row, Card, Button } from "react-bootstrap";
import ReactExport from "react-data-export";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

import { Icon } from "@cdk-uip/react-icon";
import api from "../../api/candidate.resource";
import { candidateActions } from "../../actions/candidateActions";
import { candidateDetailsConfig } from "./CandidateReportConfig";
import { prevOrgCandidatesDetailsConfig } from "./PrevOrgCandidatesDetailsConfig"
import { CANDIDATE_STATUS } from "../../constants/constants";
import {
  getFormattedDate,
  getStatusDescription
} from "../../util/helpers";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;

const OnbaoardingCandidatesReport = ({
  genders,
  maritalStatusList,
  states,
  getGendersList,
  getMaritalStatusList,
  getStatesList
}) => {
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(true);
  const [candidatesList, setCandidatesList] = useState();
  const [candidateStatus, setCandidateStatus] = useState(
    CANDIDATE_STATUS.JOINED.id
  );
  const [fromDate, setFromDate] = useState(moment().subtract(7, "days"));
  const [toDate, setToDate] = useState(moment());
  const [excelData, setExcelData] = useState(candidateDetailsConfig());
  const [prevOrgCandidatesExcelData, setPrevOrgCandidatesExcelData] = useState(
    prevOrgCandidatesDetailsConfig()
  );

  const getAllCandidatesList = async () => {
    try {
      const response = await api.getCandidatesJoiningList();
      setCandidatesList(response);
    } catch (error) {
      console.log("Error while fetching candidates list :>> ", error);
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  useEffect(() => {
    getAllCandidatesList();
    getGendersList();
    getMaritalStatusList();
    getStatesList();
  }, []);

  useEffect(() => {
    setExcelData(
      candidateDetailsConfig(
        candidatesList,
        candidateStatus,
        fromDate,
        toDate,
        states,
        genders,
        maritalStatusList
      )
    );
  }, [candidatesList, candidateStatus, fromDate, toDate]);

  useEffect(() => {
    setPrevOrgCandidatesExcelData(
      prevOrgCandidatesDetailsConfig(
        candidatesList,
        candidateStatus,
        fromDate,
        toDate
      )
    );
  }, [candidatesList, candidateStatus, fromDate, toDate]);

  const handleCandidateStatus = ({ target: { value } }) => {
    setCandidateStatus(value ? Number(value) : value);
    // Since joining date is not available until candidate is Onboarding.
    if (Number(value) < CANDIDATE_STATUS.ONBOARDING.id) {
      setFromDate(null);
      setToDate(null);
    } else if (Number(value) >= CANDIDATE_STATUS.ONBOARDING.id) {
      // set default date values for Onboarding and Joined status.
      setFromDate(moment().subtract(7, "days"));
      setToDate(moment());
    }
  };

  const getValidDateFormat = dateValue => {
    const isValidDate = moment(dateValue).isValid();
    return isValidDate ? moment(dateValue).format("YYYY-MM-DD") : null;
  };

  const handleStartDateChange = evt => {
    setFromDate(getValidDateFormat(evt.target.value.trimStart()));
  };

  const handleEndDateChange = evt => {
    setToDate(getValidDateFormat(evt.target.value.trimStart()));
  };

  const isJoiningDateAvailable = [
    CANDIDATE_STATUS.ONBOARDING.id,
    CANDIDATE_STATUS.JOINED.id
  ].includes(candidateStatus);

  return (
    <Fragment>
      <Link className="text-decoration-none d-flex ml-5 back-link" to="/CandidateList">
        <Icon size="18" className="onboarding-list-arrow-back">
          arrow_back
        </Icon>
        <h6 className="ml-2">Back to Candidate List</h6>
      </Link>
      <Form className="w-60 pt-3 m-auto">
        <Card className="mt-2 w-75 m-auto">
          <Card.Header className="text-center">
            Onboarding & Previous Organizations Candidates
            Reports
          </Card.Header>
          <Card.Body>
            <Form.Group as={Row} controlId="candidateStatus">
              <Form.Label column sm={3} className="text-right">
                Candidates Status
              </Form.Label>
              <Col sm={4}>
                <Form.Control
                  size="sm"
                  as="select"
                  disabled={isLoadingCandidates}
                  name="candidateStatus"
                  value={candidateStatus}
                  onChange={handleCandidateStatus}
                >
                  <option value="">Choose...</option>
                  {Object.values(CANDIDATE_STATUS).map(status => (
                    <option value={status.id}>{status.description}</option>
                  ))}
                </Form.Control>
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="fromDate">
              <Form.Label column sm={3} className="text-right">
                Joining Date: From
              </Form.Label>
              <Col sm={4}>
                <Form.Control
                  disabled={!isJoiningDateAvailable || isLoadingCandidates}
                  size="sm"
                  type="date"
                  name="fromDate"
                  onChange={handleStartDateChange}
                  value={getFormattedDate(fromDate, undefined, "YYYY-MM-DD")}
                  format="yy-mm-dd"
                  max={toDate}
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="toDate">
              <Form.Label column sm={3} className="text-right">
                To
              </Form.Label>
              <Col sm={4}>
                <Form.Control
                  disabled={!isJoiningDateAvailable || isLoadingCandidates}
                  size="sm"
                  type="date"
                  name="toDate"
                  onChange={handleEndDateChange}
                  value={getFormattedDate(toDate, undefined, "YYYY-MM-DD")}
                  min={fromDate}
                />
              </Col>
            </Form.Group>
            {isLoadingCandidates && (
              <p className="text-center m-4">Loading ...</p>
            )}
            {!isLoadingCandidates && !excelData[0]?.data.length && (
              <p className="text-center m-4">
                No matching records found as per selected criteria.
              </p>
            )}
            <Form.Group as={Row} className="justify-content-center mt-4">
              <Col className="d-flex justify-content-center">
                <ExcelFile
                  element={
                    <Button
                      disabled={
                        isLoadingCandidates || !excelData[0]?.data.length
                      }
                    >
                      Download Report
                    </Button>
                  }
                  filename={[
                    getStatusDescription(candidateStatus),
                    "Candidates-Report",
                    getFormattedDate(fromDate),
                    getFormattedDate(toDate)
                  ]
                    .filter(entity => entity)
                    .join("-")}
                >
                  <ExcelSheet dataSet={excelData} name="Organization" />
                </ExcelFile>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="justify-content-center mt-4">
              <Col className="d-flex justify-content-center">
                <ExcelFile
                  element={
                    <Button
                      disabled={
                        isLoadingCandidates || !prevOrgCandidatesExcelData[0]?.data.length
                      }
                    >
                      Download Previous Organizations Report
                    </Button>
                  }
                  filename={[
                    getStatusDescription(candidateStatus),
                    "Previous-Organizations-Candidates-Report",
                    getFormattedDate(fromDate),
                    getFormattedDate(toDate)
                  ]
                    .filter(entity => entity)
                    .join("-")}
                >
                  <ExcelSheet
                    dataSet={prevOrgCandidatesExcelData}
                    name="Organization"
                  />
                </ExcelFile>
              </Col>
            </Form.Group>
          </Card.Body>
          <Card.Footer>
            <b>Notes</b> :
            <ul>
              <li>
                Joined Candidates Report for a week is available to download by
                Default.
              </li>
              <li>
                All Candidates details can be downloaded by selecting the first
                option of Candidate Status.
              </li>
              <li>
                Both dates to be same in order to get the candidates details on
                a particular day.
              </li>
            </ul>
          </Card.Footer>
        </Card>
      </Form>
    </Fragment>
  );
};

const mapStateToProps = ({ candidateDetails }) => ({
  genders: candidateDetails.genders || [],
  maritalStatusList: candidateDetails.maritalStatusList || [],
  states: candidateDetails.states || []
});

const mapDispatchToProps = {
  getGendersList: candidateActions.getGendersList,
  getMaritalStatusList: candidateActions.getMaritalStatusList,
  getStatesList: candidateActions.getStatesList
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OnbaoardingCandidatesReport);
